import { VocalLib, type PitchFrame } from '@tunasong/music-lib'
import { useRef, useState } from 'react'

/**
 * The live view of a capture window, for the input meter.
 *
 * Two things the meter needs and a single frame cannot give it: the most recent frame, and the
 * noise floor the voicing gate will be measured against. The floor is settled during the lead-in
 * and then left alone — if it kept tracking, the singer's own voice would raise it as they sang,
 * the gate line would run away in front of them, and the meter would read "too quiet" however
 * loud they got.
 *
 * @see docs/superpowers/specs/2026-09-06-vocal-pitch-training-design.md §5.1
 */

const LEAD_IN_SECONDS = VocalLib.noiseFloorWindowMs / 1000

export interface CaptureMonitor {
  /** The most recent frame, or null before any arrived. */
  frame: PitchFrame | null
  /** The floor measured from this window's lead-in, linear RMS. */
  noiseFloorRms: number
  onFrame: (frame: PitchFrame) => void
  /** Clear the window. Each item measures its own room tone. */
  reset: () => void
}

export const useCaptureMonitor = (): CaptureMonitor => {
  const framesRef = useRef<PitchFrame[]>([])
  const [frame, setFrame] = useState<PitchFrame | null>(null)
  const [noiseFloorRms, setNoiseFloorRms] = useState(VocalLib.minNoiseFloorRms)

  const onFrame = (incoming: PitchFrame) => {
    framesRef.current.push(incoming)
    setFrame(incoming)
    /** Cheap: only the ~24 lead-in frames are ever re-measured. */
    if (incoming.time < LEAD_IN_SECONDS) {
      setNoiseFloorRms(VocalLib.noiseFloorRms(framesRef.current))
    }
  }

  const reset = () => {
    framesRef.current = []
    setFrame(null)
    setNoiseFloorRms(VocalLib.minNoiseFloorRms)
  }

  return { frame, noiseFloorRms, onFrame, reset }
}
